// hooks/useAuthIntegration.js
// REFACTORED: Stable integration between AuthContext and secureAxios
// Prevents circular dependencies and state flickering

import { useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Hook to integrate AuthContext with secureAxios interceptors
 * This ensures user-initiated API calls reset session timeout without causing state flickering
 */
export const useAuthIntegration = () => {
  const { resetSessionTimeout, isAuthenticated, isInitialized, logout } =
    useAuth();
  const registeredRef = useRef(false);

  useEffect(() => {
    // Only register once the auth context is fully initialized
    if (!isInitialized) {
      return;
    }

    // Avoid duplicate registrations
    if (registeredRef.current) {
      return;
    }

    // Import secureAxios dynamically to avoid circular dependency issues
    import("../utils/secureAxios").then((module) => {
      const secureAxios = module.default;

      // Set up request interceptor to reset session timeout on user actions
      const requestInterceptor = secureAxios.interceptors.request.use(
        (config) => {
          // Only reset timeout for user-initiated requests (not background/automated ones)
          const isUserAction = !config.headers?.["X-Background-Request"];

          // Only reset timeout for explicit user interactions, not background requests
          const isBackgroundRequest = config.headers?.["X-Background-Request"];
          const isWidgetLoad =
            config.url?.includes("/widgets/") ||
            config.url?.includes("/dashboard-widgets/");
          const isSystemCall = isBackgroundRequest || isWidgetLoad;

          if (
            isAuthenticated &&
            isUserAction &&
            !isSystemCall &&
            resetSessionTimeout
          ) {
            console.log("🔄 Explicit user action - resetting session timeout");
            setTimeout(() => resetSessionTimeout(), 0);
          }

          return config;
        },
        (error) => Promise.reject(error)
      );

      // Set up response interceptor to handle 401 errors consistently
      // const responseInterceptor = secureAxios.interceptors.response.use(
      //   (response) => response,
      //   (error) => {
      //     // Handle 401 unauthorized responses
      //     if (error.response?.status === 401 && isAuthenticated) {
      //       console.warn("🔐 401 Unauthorized - forcing logout");

      //       // Use setTimeout to avoid state update conflicts
      //       setTimeout(() => {
      //         if (logout) {
      //           logout(true); // Mark as timeout logout
      //         }
      //       }, 0);
      //     }

      //     return Promise.reject(error);
      //   }
      // );

      // FIXED: Enhanced response interceptor with proper logout protection
      const responseInterceptor = secureAxios.interceptors.response.use(
        (response) => response,
        (error) => {
          const is401Error = error.response?.status === 401;
          const isLogoutEndpoint =
            error.config?.url?.includes("/api/auth/logout");
          const isRefreshEndpoint =
            error.config?.url?.includes("/api/auth/refresh");
          const globalLogoutInProgress = window._webvenly_logout_in_progress;

          // Only trigger logout for session-related 401s, not logout/refresh endpoint failures
          if (
            is401Error &&
            isAuthenticated &&
            !isLogoutEndpoint &&
            !isRefreshEndpoint &&
            !globalLogoutInProgress
          ) {
            console.warn("🔐 401 Unauthorized - initiating logout sequence");

            // Set global flag to prevent other interceptors from triggering logout
            window._webvenly_logout_in_progress = true;

            // Use setTimeout to avoid state update conflicts and add small delay for debouncing
            setTimeout(() => {
              // Double-check that logout hasn't been triggered by another source
              if (!window._webvenly_logout_in_progress) {
                console.warn("🔄 Logout already completed by another process");
                return;
              }

              if (logout) {
                console.log("🚪 Triggering logout due to session expiration");
                logout(true); // Mark as timeout logout
              }
            }, 100); // Small delay for debouncing
          } else if (is401Error && (isLogoutEndpoint || isRefreshEndpoint)) {
            console.log(
              "ℹ️ 401 from auth endpoint - expected behavior, ignoring"
            );
          } else if (is401Error && globalLogoutInProgress) {
            console.log(
              "ℹ️ 401 during logout process - ignoring to prevent loop"
            );
          }

          return Promise.reject(error);
        }
      );

      registeredRef.current = true;
      console.log("🔗 AuthContext integrated with secureAxios interceptors");

      // Store interceptor IDs for cleanup
      window.__authInterceptors = {
        request: requestInterceptor,
        response: responseInterceptor,
      };
    });

    // Cleanup function
    return () => {
      if (window.__authInterceptors && registeredRef.current) {
        import("../utils/secureAxios").then((module) => {
          const secureAxios = module.default;

          secureAxios.interceptors.request.eject(
            window.__authInterceptors.request
          );
          secureAxios.interceptors.response.eject(
            window.__authInterceptors.response
          );

          delete window.__authInterceptors;
          registeredRef.current = false;

          // Clean up global logout flag
          window._webvenly_logout_in_progress = false;

          console.log("🔌 AuthContext disconnected from secureAxios");
        });
      }
    };
  }, [isAuthenticated, isInitialized, resetSessionTimeout, logout]);
};

/**
 * Alternative: Auto-integration component
 * Wrap your App component with this to automatically handle the integration
 */
export const AuthIntegrationProvider = ({ children }) => {
  useAuthIntegration();
  return children;
};

/**
 * Utility function to mark API requests as background/automated
 * Use this for widget updates, heartbeat calls, etc. that shouldn't reset session timeout
 */
export const createBackgroundRequestConfig = (config = {}) => ({
  ...config,
  headers: {
    ...config.headers,
    "X-Background-Request": "true",
  },
});

/**
 * Hook for making background API calls that don't interfere with session management
 */
export const useBackgroundApi = () => {
  return {
    createBackgroundConfig: createBackgroundRequestConfig,
  };
};
